import * as React from 'react';
import { ScoredSelectionStats, ISelectionStats, SurveyQuestion, assertNever } from '../../../../../../../amorg-common';
import { surveyQuestionHelper } from '../../../../../../../amorg-common/helpers/surveyQuestionHelper';
import { colors } from '../../../../../../../designSystem/colors/color';
import { SegmentRatioBar } from '../../../../../../../designSystem/graphs/SegmentRatioBar';
import { txt } from '../../../../../../../txt';

const smallHeightPx = 8;

export function NoQuestionsSurveyAnswerRatioBar():JSX.Element
{
    return <div style={{marginTop:10, fontStyle:'italic'}}>Ingen spørgsmål</div>;
}

export function NoAnswersSurveyAnswerRatioBar():JSX.Element
{
    return <div style={{marginTop:10, fontStyle:'italic'}}>Ingen besvarelser</div>;
}

export function NoScoredSurveyAnswerRatioBar():JSX.Element
{
    return <div style={{marginTop:10, fontStyle:'italic'}}>Ingen besvarelser med score</div>;
}

export function NoScoredAnswersSurveyAnswerRatioBar():JSX.Element
{
    return <div style={{marginTop:10, fontStyle:'italic'}}>Ingen spørgsmål med score</div>;
}

export function AggregatedSurveyAnswerRatioBar(
    props:{
        stats:ScoredSelectionStats
    }
): JSX.Element
{
    if(props.stats.totalSelections === 0)
        return <NoScoredSurveyAnswerRatioBar />;

    const summary = props.stats.score.summary;

    const data:{value:number, color:string, legendText:string}[] = [
        { value:summary.score1Count, color:colors.graphs.survey.score.score1, legendText:txt.SurveyScore_1 + ' ' + getLegendTextPostfix(summary.score1Count, props.stats.totalSelections) },
        { value:summary.score2Count, color:colors.graphs.survey.score.score2, legendText:txt.SurveyScore_2 + ' ' + getLegendTextPostfix(summary.score2Count, props.stats.totalSelections) },
        { value:summary.score3Count, color:colors.graphs.survey.score.score3, legendText:txt.SurveyScore_3 + ' ' + getLegendTextPostfix(summary.score3Count, props.stats.totalSelections) },
        { value:summary.score4Count, color:colors.graphs.survey.score.score4, legendText:txt.SurveyScore_4 + ' ' + getLegendTextPostfix(summary.score4Count, props.stats.totalSelections) },
        { value:summary.score5Count, color:colors.graphs.survey.score.score5, legendText:txt.SurveyScore_5 + ' ' + getLegendTextPostfix(summary.score5Count, props.stats.totalSelections) }
    ];

    return (
        <SegmentRatioBar data={data} legend/>
    );

}

export function QuestionSurveyAnswerRatioBar(
    props:{
        stats:ISelectionStats,
        question:SurveyQuestion,
        small?:boolean
    }
): JSX.Element
{
    if(props.stats.totalSelections === 0)
        return <NoAnswersSurveyAnswerRatioBar />;

    if(props.question.type === 'custom')
        return <CustomQuestionSurveyAnswerRatioBar stats={props.stats} question={props.question} small={props.small} />;
    else if(props.question.type === 'multiple-choice')
        return <CustomQuestionSurveyAnswerRatioBar stats={props.stats} question={props.question} small={props.small} />;
    else if(props.question.type === 'default')
        return <DefaultQuestionSurveyAnswerRatioBar stats={props.stats as ScoredSelectionStats} question={props.question} small={props.small} />;
    else
    {
        assertNever(props.question);
    }
}






// ---------- These are helpers ---------------------------------------------

function CustomQuestionSurveyAnswerRatioBar(
    props:{
        stats:ISelectionStats,
        question:SurveyQuestion,
        small?:boolean
    }
): JSX.Element
{
    if(props.question.type !== 'custom' && props.question.type !== 'multiple-choice')
    {
        throw new Error('Expected custom question or multiple-choice question (got ' + props.question.type + ')');
    }

    if(surveyQuestionHelper.isScoredQuestion(props.question) && !(props.stats instanceof ScoredSelectionStats))
    {
        throw new Error('Expected scored selection stats for scored question');
    }

    const options = props.question.options;

    let data:{value:number, color:string, legendText:string}[] = [];
    if(surveyQuestionHelper.isScoredQuestion(props.question))
    {
        const scoredStats = props.stats as ScoredSelectionStats;
        data = getScoredDataSet(
            scoredStats.score.summary.score1Count,
            scoredStats.score.summary.score2Count,
            scoredStats.score.summary.score3Count,
            scoredStats.score.summary.score4Count,
            scoredStats.score.summary.score5Count,
            scoredStats.totalSelections
        );
        
    }
    else {
        data = props.stats.data.optionValueCounts.map((option,index) =>
        {
            const questionOption = options.find(o => o.value === option.value);
            const legendText = (questionOption ? questionOption.text : 'N/A') + ' ' + getLegendTextPostfix(option.count, props.stats.totalSelections);

            return {
                value:option.count,
                color:colors.indexed.dark[index % colors.indexed.dark.length],
                legendText
            }
        });
    }

    return (
        <SegmentRatioBar data={data} legend heightPx={props.small ? smallHeightPx : undefined }/>
    );
}

function DefaultQuestionSurveyAnswerRatioBar(
    props:{
        stats:ScoredSelectionStats,
        question:SurveyQuestion,
        small?:boolean,
    }
): JSX.Element
{
    if(props.question.type !== 'default')
    {
        throw new Error('Expected default question');
    }

    const summary = props.stats.score.summary;

    const data = getScoredDataSet(
        summary.score1Count,
        summary.score2Count,
        summary.score3Count,
        summary.score4Count,
        summary.score5Count,
        props.stats.totalSelections
    );

    return (
        <SegmentRatioBar data={data} legend heightPx={props.small ? smallHeightPx : undefined }/>
    );

}

function getScoredDataSet(score1:number, score2:number, score3:number, score4:number, score5:number, totalSelections:number)
{
     const data:{value:number, color:string, legendText:string}[] = [
        { value:score1, color:colors.graphs.survey.score.score1, legendText:txt.SurveyScore_agreement_1 + ' '+ getLegendTextPostfix(score1, totalSelections) },
        { value:score2, color:colors.graphs.survey.score.score2, legendText:txt.SurveyScore_agreement_2 + ' ' + getLegendTextPostfix(score2, totalSelections) },
        { value:score3, color:colors.graphs.survey.score.score3, legendText:txt.SurveyScore_agreement_3 + ' ' + getLegendTextPostfix(score3, totalSelections) },
        { value:score4, color:colors.graphs.survey.score.score4, legendText:txt.SurveyScore_agreement_4 + ' ' + getLegendTextPostfix(score4, totalSelections) },
        { value:score5, color:colors.graphs.survey.score.score5, legendText:txt.SurveyScore_agreement_5 + ' ' + getLegendTextPostfix(score5, totalSelections) }
    ];

    return data;
}

function getLegendTextPostfix(count:number, totalSelections:number):string
{
    const percentage = count / totalSelections * 100;
    return ` (${percentage.toFixed(0)}%)`;
}